import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, UserCircle, Tag, Plus } from 'lucide-react';

import type { PersonaBackstory } from '../services/workshop';

interface PersonaBackstoryModalProps {
  sessionId: string;
  persona: {
    id: string;
    name: string;
    role: string;
    goals?: string[];
    painPoints?: string[];
    backstory?: PersonaBackstory;
  };
  onSave: (backstory: PersonaBackstory) => Promise<void>;
  onClose: () => void;
}

const EMPTY_BACKSTORY: PersonaBackstory = {
  age: '',
  gender: '',
  education: '',
  familySituation: '',
  background: '',
  traits: [],
};

export function PersonaBackstoryModal({ sessionId, persona, onSave, onClose }: PersonaBackstoryModalProps) {
  const [form, setForm] = useState<PersonaBackstory>(() => ({
    ...EMPTY_BACKSTORY,
    ...(persona.backstory || {}),
    traits: persona.backstory?.traits ?? [],
  }));
  const [traitInput, setTraitInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const traitInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (field: keyof PersonaBackstory, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const addTrait = () => {
    const t = traitInput.trim();
    if (!t || form.traits.includes(t)) { setTraitInput(''); return; }
    setForm(f => ({ ...f, traits: [...f.traits, t] }));
    setTraitInput('');
    traitInputRef.current?.focus();
  };

  const removeTrait = (trait: string) => {
    setForm(f => ({ ...f, traits: f.traits.filter(t => t !== trait) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch {
      setError('Failed to save backstory.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-display" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <UserCircle size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-navy leading-tight">{persona.name}</h2>
              <p className="text-xs text-slate-500">{persona.role} · Backstory</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Age</label>
              <input
                className={inputCls}
                placeholder="e.g. 40, mid-40s"
                value={form.age || ''}
                onChange={e => set('age', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Gender</label>
              <input
                className={inputCls}
                placeholder="e.g. Female"
                value={form.gender || ''}
                onChange={e => set('gender', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Education</label>
              <input
                className={inputCls}
                placeholder="e.g. Degree, Masters"
                value={form.education || ''}
                onChange={e => set('education', e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Family Situation</label>
              <input
                className={inputCls}
                placeholder="e.g. Mother of 3"
                value={form.familySituation || ''}
                onChange={e => set('familySituation', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Background</label>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Brief narrative background — context, career, life situation..."
              value={form.background || ''}
              onChange={e => set('background', e.target.value)}
            />
          </div>

          <div>
            <label className={labelCls}>
              <span className="flex items-center gap-1.5"><Tag size={11} />Personality Traits</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
              {form.traits.map(trait => (
                <span
                  key={trait}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold border border-primary/20"
                >
                  {trait}
                  <button
                    type="button"
                    onClick={() => removeTrait(trait)}
                    className="hover:text-red-500 transition-colors ml-0.5"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                ref={traitInputRef}
                className={`${inputCls} flex-1`}
                placeholder="Type a trait and press Enter (e.g. shy, detail-oriented)"
                value={traitInput}
                onChange={e => setTraitInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrait(); } }}
              />
              <button
                type="button"
                onClick={addTrait}
                disabled={!traitInput.trim()}
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary disabled:opacity-40 transition-colors flex-shrink-0"
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">Press Enter or click + to add each trait</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-[#e2e8f0] bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            Save Backstory
          </button>
        </div>
      </div>
    </div>
  );
}
